/* eslint-disable consistent-return */
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import {
  forwardRef,
  MutableRefObject,
  useEffect,
  useRef
} from 'react';
import type { ScrollBarProps as PerfectScrollbarProps } from 'react-perfect-scrollbar';
import PerfectScrollbar from 'react-perfect-scrollbar';

interface ScrollbarProps extends PerfectScrollbarProps {
  fixedX?: boolean;
}
const MIN_SCROLL_LEFT = 0;

const useStyles = makeStyles({
  container: {
    position: 'relative',
    overflowX: 'hidden',
  },
  tableContainer: {
    overflowX: 'auto',
    overflowY: 'visible',
    '&::-webkit-scrollbar': { display: 'none' },
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  track: {
    position: 'fixed',
    bottom: 0,
    height: 16,
    overflowX: 'auto',
    overflowY: 'hidden',
    zIndex: 1103,
    backgroundColor: '#e4dada',
    '& > div': { height: 1 },
  },
  mobileWrapper: {
    overflowX: 'auto',
    overflowY: 'visible',
  },
});

const Scrollbar = forwardRef<HTMLDivElement, ScrollbarProps>((props, ref) => {
  const { children, fixedX = false, ...other } = props;
  const classes = useStyles();
  const tableRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Helper: turn the forwarded `ref` into a callback
  const handleContainerRef = (el: any | null) => {
    if (!ref) return;
    if (typeof ref === 'function') {
      ref(el);
    } else {
      (ref as MutableRefObject<HTMLElement | null>).current = el;
    }
  };

  // detect mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
    .test(navigator.userAgent);

  // ─── fixedX logic ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!fixedX) return;
    const tableEl = tableRef.current!;
    const trackEl = trackRef.current!;
    const inner = trackEl.firstElementChild as HTMLElement;

    const updateScrollbar = () => {
      inner.style.width = `${tableEl.scrollWidth}px`;
      const rect = tableEl.getBoundingClientRect();
      trackEl.style.left = `${rect.left}px`;
      trackEl.style.width = `${rect.width}px`;
    };

    const onTableScroll = () => {
      const left = Math.max(tableEl.scrollLeft, MIN_SCROLL_LEFT);
      trackEl.scrollLeft = left;
    };
    const onTrackScroll = () => {
      const left = Math.max(trackEl.scrollLeft, MIN_SCROLL_LEFT);
      tableEl.scrollLeft = left;
    };

    updateScrollbar();
    tableEl.addEventListener('scroll', onTableScroll);
    trackEl.addEventListener('scroll', onTrackScroll);
    window.addEventListener('resize', updateScrollbar);
    window.addEventListener('scroll', updateScrollbar, true);
    const ro = new ResizeObserver(updateScrollbar);
    ro.observe(tableEl);

    return () => {
      tableEl.removeEventListener('scroll', onTableScroll);
      trackEl.removeEventListener('scroll', onTrackScroll);
      window.removeEventListener('resize', updateScrollbar);
      window.removeEventListener('scroll', updateScrollbar, true);
      ro.disconnect();
    };
  }, [fixedX]);

  // ─── non-fixedX render ─────────────────────────────────────────────────────────
  if (!fixedX) {
    // Mobile: just native scroll
    if (isMobile) {
      return (
        <div
          ref={ref}
          className={classes.mobileWrapper}
        >
          <div ref={tableRef}>{children}</div>
        </div>
      );
    }
    // Desktop: PerfectScrollbar → **containerRef must be a function** :contentReference[oaicite:1]{index=1}
    return (
      <PerfectScrollbar
        {...other}
        containerRef={handleContainerRef}
      >
        <div ref={tableRef}>{children}</div>
      </PerfectScrollbar>
    );
  }

  // ─── fixedX render ──────────────────────────────────────────────────────────────
  return (
    <div
      ref={ref}
      className={classes.container}
    >
      {/* pinned-to-viewport-bottom fake scrollbar */}
      <div
        ref={trackRef}
        className={classes.track}
      >
        <div />
      </div>

      {/* actual table scroll area */}
      <div
        ref={tableRef}
        className={classes.tableContainer}
      >
        {children}
      </div>
    </div>
  );
});

Scrollbar.propTypes = {
  children: PropTypes.node,
  fixedX: PropTypes.bool,
};

export default Scrollbar;
